import React, { useEffect, useRef, useState } from "react";

import HomeDashboardLayout from "../HomeDashboardLayout";
import GamificationScoreBoard from "./GamificationScoreBoard";
import HeaderProfileSection from "../HeaderProfileSection";
import CountdownTimer from "./CountdownTimer";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QbankDetail.css";

const questionOptions = [
  {
    id: "A",
    tone: "neutral",
    english: "Dravya,Guna,Karma, Samavaya, Vishesha, Samavaya",
    hindi: "द्रव्य, गुण, कर्म, समवाय, विशेष, समवाय",
  },
  {
    id: "B",
    tone: "correct",
    english: "Samnya, Vishesha, Guna, Dravya, Karma, Samavaya",
    hindi: "सामान्य, विशेष, गुण, द्रव्य, कर्म, समवाय",
  },
  {
    id: "C",
    tone: "wrong",
    english: "Samanya, Vishesha, Samavaya, Dravya, Guna, Karma",
    hindi: "सामान्य, विशेष, समवाय, द्रव्य, गुण, कर्म",
  },
  {
    id: "D",
    tone: "neutral",
    english: "Dravya, Guna, Karma, Samavaya, Samanya, Vishesha",
    hindi: "द्रव्य, गुण, कर्म, समवाय, सामान्य, विशेष",
  },
];

const toolbarActions = [
  {
    icon: "bi-arrow-clockwise",
    label: "Reset question",
  },
  {
    icon: "bi-type",
    label: "Text settings",
  },
  {
    icon: "bi-info-circle",
    label: "Question info",
  },
];

const GamificationDetail = () => {


  /* =====================================================
     SCOREBOARD STATES
  ===================================================== */

  const [isScoreBoardOpen, setIsScoreBoardOpen] = useState(false);

  const [sheetHeight, setSheetHeight] = useState(
    Math.min(window.innerHeight * 0.62, 620),
  );

  const [isDraggingSheet, setIsDraggingSheet] = useState(false);

  /* =====================================================
     DRAG REFS
  ===================================================== */

  const dragStartY = useRef(0);

  const dragStartHeight = useRef(0);

  const currentHeight = useRef(0);

  /* =====================================================
     OTHER STATES
  ===================================================== */

  const [isExplanationOpen, setIsExplanationOpen] = useState(true);

  const [isPYPOpen, setIsPYPOpen] = useState(true);

  /* =====================================================
     OPEN SCOREBOARD
  ===================================================== */

  const openScoreBoard = () => {
    const initialHeight = Math.min(window.innerHeight * 0.62, 620);

    setSheetHeight(initialHeight);

    currentHeight.current = initialHeight;

    setIsScoreBoardOpen(true);
  };

  /* =====================================================
     CLOSE SCOREBOARD
  ===================================================== */

  const closeScoreBoard = () => {
    setIsDraggingSheet(false);

    setIsScoreBoardOpen(false);
  };

  /* =====================================================
     DRAG START
  ===================================================== */

  const handleSheetPointerDown = (e) => {
    dragStartY.current = e.clientY;

    dragStartHeight.current = sheetHeight;

    currentHeight.current = sheetHeight;

    setIsDraggingSheet(true);

    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  /* =====================================================
     DRAG MOVE
  ===================================================== */

  const handleSheetPointerMove = (e) => {
    if (!isDraggingSheet) return;

    /*
      Drag calculation

      Drag UP:
      clientY decreases
      difference becomes positive
      height increases

      Drag DOWN:
      clientY increases
      difference becomes negative
      height decreases
    */

    const difference = dragStartY.current - e.clientY;

    const maxHeight = window.innerHeight * 0.92;

    let newHeight = dragStartHeight.current + difference;

    /*
      Allow downward movement below minimum
      so user can drag and close.
    */

    if (newHeight > maxHeight) {
      newHeight = maxHeight;
    }

    if (newHeight < 0) {
      newHeight = 0;
    }

    currentHeight.current = newHeight;

    setSheetHeight(newHeight);
  };

  /* =====================================================
     DRAG END
  ===================================================== */

  const handleSheetPointerUp = () => {
    if (!isDraggingSheet) return;

    const minimumCloseHeight = 180;

    const minimumSheetHeight = 260;

    const maximumSheetHeight = window.innerHeight * 0.92;

    /*
      Close if dragged sufficiently down
    */

    if (currentHeight.current < minimumCloseHeight) {
      closeScoreBoard();

      return;
    }

    /*
      Snap back to minimum height
    */

    if (currentHeight.current < minimumSheetHeight) {
      currentHeight.current = minimumSheetHeight;

      setSheetHeight(minimumSheetHeight);
    }

    /*
      Maximum height protection
    */

    if (currentHeight.current > maximumSheetHeight) {
      currentHeight.current = maximumSheetHeight;

      setSheetHeight(maximumSheetHeight);
    }

    setIsDraggingSheet(false);
  };

  /* =====================================================
     POINTER CANCEL
  ===================================================== */

  const handleSheetPointerCancel = () => {
    const minimumSheetHeight = 260;

    if (currentHeight.current < minimumSheetHeight) {
      currentHeight.current = minimumSheetHeight;

      setSheetHeight(minimumSheetHeight);
    }

    setIsDraggingSheet(false);
  };

  /* =====================================================
     WINDOW RESIZE
  ===================================================== */

  useEffect(() => {
    const handleResize = () => {
      const maxHeight = window.innerHeight * 0.92;

      if (sheetHeight > maxHeight) {
        setSheetHeight(maxHeight);

        currentHeight.current = maxHeight;
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [sheetHeight]);

  /* =====================================================
     FOOTER SCORE BUTTON
  ===================================================== */

  const renderFooterNav = (variant) => (
    <div className={`footer-nav-fixed ${variant}`}>
      <div
        className="
          qbank-navRow
          score-fixed-btn
          score-mobile-btn
        "
        aria-label="Question navigation"
      >
        <button
          type="button"
          className="
            qbank-navBtn
            qbank-navBtn--next
          "
          onClick={openScoreBoard}
        >
          <span>Score</span>

          <i className="bi bi-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* =====================================================
          PAGE FIXED HEIGHT
      ===================================================== */}

      <style>
        {`
          .homeDashboard-wrapper .main-content {
            height: 100vh;
            overflow: hidden;
            box-sizing: border-box;
            padding-bottom: 35px;
          }

          .homeDashboard-wrapper .page-content {
            height: 100%;
            overflow: hidden;
            padding-right: 0;
          }
        `}
      </style>

      <HomeDashboardLayout showHeader={false}>
        <div
          className="
            categories-page
            qbank-detail-page
          "
          style={{
            background: "#fff",
          }}
        >
          {/* =====================================================
              PAGE HEADER
          ===================================================== */}

          <div className="categories-page-header">
            {/* BACK */}

            <div>
              <button
                type="button"
                className="
                  categories-back-button
                "
              >
                <i
                  className="
                    bi
                    bi-chevron-left
                  "
                />
              </button>
            </div>

            {/* TITLE */}

            <div>
              <h1
                className="
                  categories-page-title
                "
              >

         {/* Existing Group Battle Countdown */}
            <CountdownTimer
              initialTime={22}
              isActive={true}
            />
              </h1>
            </div>

            {/* PROFILE */}

            <div>
              <HeaderProfileSection />
            </div>
          </div>

          {/* =====================================================
              MAIN PAGE CONTENT
          ===================================================== */}

          <div className="qb-Detail-content scoreboard-sidebar">
            <div className="d-flex qbank-detail-main">
              {/* =================================================
                  LEFT QUESTION CONTENT
              ================================================= */}

              <section
                className="qbank-LeftContent"
                aria-label="
                  Question and answers
                "
              >
                <div
                  className="
                    qbank-card
                    qbank-card--question
                  "
                >
                  {/* QUESTION TOP */}

                  <div className="qbank-questionTop">
                    {/* QUESTION COUNT */}

                    <span
                      className="
                        qbank-progress
                      "
                    >
                      1 of 192
                    </span>

                    {/* TIMER */}

                    <div
                      className="
                        qbank-timer
                      "
                      aria-label="
                        Question timer
                      "
                    >
                      <i
                        className="
                          bi
                          bi-clock
                        "
                        aria-hidden="true"
                      />

                      <span>00:00:20</span>
                    </div>

                    {/* TOOLBAR */}

                    <div
                      className="
                        qbank-toolbar
                      "
                      aria-label="
                        Question tools
                      "
                    >
                      {toolbarActions.map((action) => (
                        <button
                          key={action.label}
                          type="button"
                          className="
                              qbank-toolbarBtn
                            "
                          aria-label={action.label}
                        >
                          <i
                            className={`bi ${action.icon}`}
                            aria-hidden="true"
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* =================================================
                      QUESTION SCROLL AREA
                  ================================================= */}

                  <div
                    className="
                      data-question-scroll
                    "
                  >
                    {/* QUESTION */}

                    <div
                      className="
                        qbank-questionBlock
                      "
                    >
                      <h2
                        className="
                          qbank-question
                        "
                      >
                        According to acharya charaka, the sequence of shat
                        padartha is -
                      </h2>

                      <p
                        className="
                          qbank-questionSub
                        "
                      >
                        आचार्य चरक के अनुसार, षट् पदार्थों का क्रम है -
                      </p>
                    </div>

                    {/* OPTIONS */}

                    <div
                      className="
                        qbank-options
                      "
                      role="list"
                      aria-label="
                        Answer options
                      "
                    >
                      {questionOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`
                              qbank-option
                              qbank-option--${option.tone}
                              `}
                          aria-label={`Option ${option.id}`}
                        >
                          {/* BADGE */}

                          <span
                            className="
                                qbank-option__badge
                              "
                          >
                            {option.id}
                          </span>

                          {/* OPTION TEXT */}

                          <span
                            className="
                                qbank-option__copy
                              "
                          >
                            <span
                              className="
                                  qbank-option__english
                                "
                            >
                              {option.english}
                            </span>

                            <span
                              className="
                                  qbank-option__hindi
                                "
                            >
                              {option.hindi}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* GUESS ANSWER */}

                    <label
                      className="
                        qbank-guess
                      "
                      htmlFor="
                        guess-answer
                      "
                    >
                      <span
                        className="
                          qbank-guess__check
                        "
                      >
                        <input id="guess-answer" type="checkbox" />

                        <span
                          className="
                            qbank-guess__box
                          "
                          aria-hidden="true"
                        />
                      </span>

                      <span
                        className="
                          qbank-guess__label
                        "
                      >
                        Guess Answer
                      </span>

                      <div
                        className="
                          info-btn
                        "
                      >
                        <i
                          className="
                            bi
                            bi-info-circle-fill
                          "
                        />
                      </div>
                    </label>
                  </div>

                  {/* MOBILE SCORE BUTTON */}

                  {renderFooterNav("footer-nav-fixed--desktop")}
                </div>
              </section>

              {/* =================================================
                  RIGHT CONTENT
              ================================================= */}

              <aside
                className="
                  qbank-rightContent
                "
                aria-label="
                  Explanation and notes
                "
              >
                {/* PYP */}

                <section
                  className="
                    qbank-sideCard
                  "
                >
                  <div
                    className="
                      qbank-sideCard__head
                    "
                  >
                    <h3>PYP</h3>

                    <button
                      type="button"
                      className="
                        qbank-sideCard__toggle
                      "
                      aria-label="
                        Toggle PYP
                      "
                      onClick={() => setIsPYPOpen(!isPYPOpen)}
                    >
                      <i
                        className={`bi ${
                          isPYPOpen ? "bi-chevron-up" : "bi-chevron-down"
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  {isPYPOpen && (
                    <div
                      className="
                        qbank-sideCard__body
                      "
                    >
                      <div
                        className="
                          qbank-sideCard__subtitle
                        "
                      >
                        <span>Charaka sutra 1/28</span>

                        <span>चक सूत्र 1/28</span>
                      </div>
                    </div>
                  )}
                </section>

                {/* EXPLANATION */}

                <section
                  className="
                    qbank-sideCard
                  "
                >
                  <div
                    className="
                      qbank-sideCard__head
                    "
                  >
                    <h3>Explanation</h3>

                    <button
                      type="button"
                      className="
                        qbank-sideCard__toggle
                      "
                      aria-label="
                        Toggle Explanation
                      "
                      onClick={() => setIsExplanationOpen(!isExplanationOpen)}
                    >
                      <i
                        className={`bi ${
                          isExplanationOpen
                            ? "bi-chevron-up"
                            : "bi-chevron-down"
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  {isExplanationOpen && (
                    <div
                      className="
                        qbank-sideCard__body
                      "
                    >
                      <div
                        className="
                          qbank-sideCard__subtitle
                        "
                      >
                        <span>Charaka sutra 1/28</span>

                        <span>चक सूत्र 1/28</span>
                      </div>
                    </div>
                  )}
                </section>
              </aside>
            </div>
               {renderFooterNav("footer-nav-fixed--mobile")}
            {/* =================================================
                DESKTOP SCOREBOARD
            ================================================= */}

            <div
              className="
                Qbank-sidebar
                qbank-detail-sidebar
                qbank-detail-sidebar--desktop
              "
            >
              <GamificationScoreBoard />
            </div>
          </div>

          {/* =====================================================
              MOBILE SCOREBOARD BOTTOM SHEET
          ===================================================== */}

          {isScoreBoardOpen && (
            <div
              className="
                scoreboard-mobile-overlay
              "
              onClick={closeScoreBoard}
            >
              <div
                className="
                  scoreboard-mobile-sheet
                "
                style={{
                  height: `${sheetHeight}px`,

                  transition: isDraggingSheet
                    ? "none"
                    : "height 0.28s ease-out",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* =============================================
                    DRAG HANDLE AREA
                ============================================= */}

                <div
                  className="
                    scoreboard-sheet-drag-area
                  "
                  onPointerDown={handleSheetPointerDown}
                  onPointerMove={handleSheetPointerMove}
                  onPointerUp={handleSheetPointerUp}
                  onPointerCancel={handleSheetPointerCancel}
                >
                  <div
                    className="
                      scoreboard-sheet-handle
                    "
                  />
                </div>

                {/* CLOSE BUTTON */}

                <button
                  type="button"
                  className="
                    scoreboard-sheet-close
                  "
                  aria-label="
                    Close Scoreboard
                  "
                  onClick={closeScoreBoard}
                >
                  <i className="bi bi-x-lg" />
                </button>

                {/* SCOREBOARD */}

                <div
                  className="
                    scoreboard-mobile-content
                  "
                >
                  <GamificationScoreBoard />
                </div>
              </div>
            </div>
          )}
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default GamificationDetail;
